import React, { useContext, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/UserContext';
import { useTagStore } from '../stores/tagStore';
import { ConfirmModal, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, CustomSelect, BulkSelectCell, RowActions, RowAction, ListPagination, ListSearchInput, ListEmptyRow, BulkSelectHeader, MultiBulkActionButton, btnPrimary, btnSecondary } from '@redpanda/ui';
import { formatTableDate, useRememberedFlag } from '@redpanda/utils';
import TagDialog from '../components/TagDialog';
import { createDefaultTags, bulkDeleteTags, getTags } from '../services/api';
import { useListSearch } from '../hooks/useListSearch';
import { useDataChanged } from '../utils/dataEvents';
import { toast } from 'react-hot-toast';
import { useBulkActions, isAdmin } from '@redpanda/utils';
import { createBulkDeleteOptimisticHandler, createBulkDeleteSuccessHandler } from '@redpanda/utils';
import { useTagStoreBase, notifyTagsChanged, type Tag } from '../stores/tagStore';
import { TableSkeleton } from '@redpanda/ui';

const TagListPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const userContext = useContext(UserContext);
  const { isAuthenticated, user, isLoading: isUserLoading } = userContext!;
  const currentUserId = user?.id;
  const userRole = user?.role;
  
  const {
    tags,
    pagination,
    error,
    isPageLoading,
    showDeleteConfirmModal,
    isDeleting,
    setCurrentPage,
    handleDelete,
    confirmDelete,
    closeDeleteModal,
    fetchTags,
  } = useTagStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [isInitializingDefaults, setIsInitializingDefaults] = useState(false);
  const [showTagDialog, setShowTagDialog] = useState(false);
  const [showColorColumn, setShowColorColumn] = useRememberedFlag('crm-ui:tags.colorColumn', false);
  const [showCreatedAtColumn, setShowCreatedAtColumn] = useRememberedFlag('crm-ui:tags.createdAtColumn', false);
  const [editingTag, setEditingTag] = useState<{
    id: string;
    name: string;
    category: string;
    color: string;
  } | null>(null);

  const categoryOptions = [
    { value: '', label: t('common.forms.all') },
    { value: 'INDUSTRY', label: t('common.categories.industry') },
    { value: 'SIZE', label: t('common.categories.size') },
    { value: 'STATUS', label: t('common.categories.status') },
    { value: 'CUSTOM', label: t('common.categories.custom') },
  ];

  // 使用 ref 來穩定化 fetchTags 引用，避免無限循環
  const fetchTagsRef = useRef(fetchTags);
  fetchTagsRef.current = fetchTags;

  useEffect(() => {
    if (isAuthenticated && currentUserId) {
      // 頁面初始化時使用快取（如果有），避免每次切換都重新載入
      fetchTagsRef.current(1, false);
    }
  }, [isAuthenticated, currentUserId]);

  // 搜尋、分類篩選由後端查全部標籤（原本只過濾目前這一頁）
  const search = searchTerm.trim();
  const hasFilters = !!search || !!categoryFilter;
  const query = { search: search || undefined, category: categoryFilter || undefined };
  const searchResult = useListSearch<Tag>('tags', hasFilters ? query : null, page => getTags(page, query));
  const filteredTags = searchResult.active ? searchResult.items : tags;
  const displayPagination = searchResult.active ? searchResult.pagination : pagination;
  const listLoading = searchResult.active ? searchResult.isLoading : isPageLoading;

  // 標籤選擇器新增標籤、對話框編輯後重新載入目前這一頁
  useDataChanged('tags', () => {
    if (!searchResult.active) fetchTags(pagination?.currentPage ?? 1, true);
  });

  const getCategoryText = (category: string) => {
    switch (category) {
      case 'INDUSTRY': return t('common.categories.industry');
      case 'SIZE': return t('common.categories.size');
      case 'STATUS': return t('common.categories.status');
      case 'CUSTOM': return t('common.categories.custom');
      default: return category;
    }
  };

  // 檢查選中的標籤中是否有被使用的
  const hasUsedTagsSelected = (selectedIds: string[]) => {
    return selectedIds.some(id => {
      const tag = tags?.find(t => t.id === id);
      return tag?._count && tag._count.assignments > 0;
    });
  };

  // 初始化預設標籤
  const handleInitDefaults = async () => {
    if (!isAuthenticated || !currentUserId) {
      toast.error(t('common.messages.unauthorized'));
      return;
    }

    setIsInitializingDefaults(true);
    try {
      const response = await createDefaultTags(i18n.language);
      // 後端回傳翻譯 key：defaultTagsExist／defaultTagsCreated
      if (response.data.message === 'defaultTagsExist') {
        toast.success(t('tags.add.defaultTagsExist', { ns: 'pages' }));
      } else {
        toast.success(t('tags.list.initializeSuccess', { count: response.data.data.length, ns: 'pages' }));
      }
      notifyTagsChanged();
    } catch (error: any) {
      console.error('初始化預設標籤失敗:', error);
      toast.error(error.response?.data?.error || t('tags.list.initializeFailed', { ns: 'pages' }));
    } finally {
      setIsInitializingDefaults(false);
    }
  };

  const bulkDeleteSuccess = createBulkDeleteSuccessHandler(() => useTagStoreBase.getState(), fetchTags, setCurrentPage);

  // 使用新的批次操作 hook
  const {
    selectedItems: selectedTags,
    showBulkDeleteModal,
    isBulkDeleting,
    isItemSelected,
    allSelected,
    toggleItemSelection,
    toggleSelectAll,
    handleBulkDelete,
    confirmBulkDelete,
    closeBulkDeleteModal,
    handlePageChange
  } = useBulkActions({
    items: filteredTags,
    idField: 'id',
    entityName: t('tags.item', { ns: 'pages' }),
    bulkDeleteApi: bulkDeleteTags,
    onOptimisticUpdate: createBulkDeleteOptimisticHandler(
      useTagStoreBase.setState,
      'tag',
      () => tags,
      () => pagination
    ),
    onSuccess: async (deletedIds?: string[]) => {
      await bulkDeleteSuccess(deletedIds ?? []);
      notifyTagsChanged();
    },
    userRole,
    t,
    toast,
    // 自定義檢查項目是否可以刪除
    canDeleteItem: (item: any) => !(item._count && item._count.assignments > 0),
    // 智能分頁配置
    pagination: displayPagination ? {
      currentPage: displayPagination.currentPage,
      totalPages: displayPagination.totalPages
    } : undefined,
    fetchItems: fetchTags,
    setCurrentPage
  });

  const changePage = (page: number) => (searchResult.active ? searchResult.setPage(page) : handlePageChange(page));

  // 處理編輯標籤
  const handleEditTag = (tag: any) => {
    setEditingTag({
      id: tag.id,
      name: tag.name,
      category: tag.category,
      color: tag.color
    });
    setShowTagDialog(true);
  };

  // 處理新增標籤
  const handleAddTag = () => {
    setEditingTag(null);
    setShowTagDialog(true);
  };

  // 關閉標籤對話框
  const handleCloseTagDialog = () => {
    setShowTagDialog(false);
    setEditingTag(null);
  };

  // 如果用戶還在載入中，先不顯示任何內容
  if (isUserLoading) {
    return null; 
  }

  // 如果用戶未認證，顯示錯誤
  if (!isAuthenticated || !currentUserId) {
    return <div className="p-8 text-center text-red-500">{t('common.messages.unauthorized')}</div>;
  }

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('tags.list.title', { ns: 'pages' })}</h1>
          {isAdmin(userRole) && (
            <div className="flex space-x-3">
              {/* 批量操作按鈕 */}
              <MultiBulkActionButton
                selectedCount={selectedTags.length}
                isDeleting={isBulkDeleting}
                onDelete={handleBulkDelete}
                deleteDisabled={hasUsedTagsSelected(selectedTags)}
                deleteDisabledMessage={t('tags.list.bulkDeleteHasUsed', { ns: 'pages' })}
              />
              <button
                type="button"
                disabled={isInitializingDefaults}
                onClick={handleInitDefaults}
                className={btnSecondary}
              >
                {isInitializingDefaults ? t('common.status.loading') : t('tags.list.initializeDefaults', { ns: 'pages' })}
              </button>
              <button
                onClick={() => setShowColorColumn(!showColorColumn)}
                aria-pressed={showColorColumn}
                className={btnSecondary}
              >
                {showColorColumn ? t('tags.list.hideColorCodes', { ns: 'pages' }) : t('tags.list.showColorCodes', { ns: 'pages' })}
              </button>
              <button
                onClick={() => setShowCreatedAtColumn(!showCreatedAtColumn)}
                aria-pressed={showCreatedAtColumn}
                className={btnSecondary}
              >
                {showCreatedAtColumn ? t('tags.list.hideCreatedAt', { ns: 'pages' }) : t('tags.list.showCreatedAt', { ns: 'pages' })}
              </button>
              <button 
                onClick={handleAddTag}
                className={btnPrimary}
              >
                {t('tags.list.addButton', { ns: 'pages' })}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 搜尋和篩選區域 */}
      <div className="mb-4 flex flex-col md:flex-row md:items-center gap-3">
        <ListSearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t('tags.search.placeholder', { ns: 'pages' })} />
        <div className="md:w-64">
          <CustomSelect
            value={categoryFilter}
            onChange={(value) => setCategoryFilter(value)}
            placeholder={t('tags.search.filterPlaceholder', { ns: 'pages' })}
            className="h-10"
            options={
              categoryOptions.filter(option => option.value !== '')
            }
          />
        </div>
        {/* 清除篩選：只在有篩選條件時出現（原本選了分類就清不掉） */}
        {hasFilters && (
          <button
            type="button"
            onClick={() => { setSearchTerm(''); setCategoryFilter(''); }}
            className="self-start md:self-auto text-sm font-medium text-fg-secondary hover:text-fg transition-colors"
          >
            {t('common.filters.clear')}
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-4 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg">
          {error}
        </div>
      )}

      {/* 表格上方的分頁控制 */}
      <ListPagination pagination={displayPagination} onPageChange={changePage} isLoading={listLoading} className="mb-3" />

      {/* 骨架屏載入狀態 */}
      {listLoading ? (
        <TableSkeleton rows={10} columns={6} showHeader={true} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <Table style={{ tableLayout: 'fixed', minWidth: '800px' }}>
            <TableHeader>
              <TableRow>
                <BulkSelectHeader
                  userRole={userRole}
                  allSelected={allSelected}
                  onToggleAll={toggleSelectAll}
                  hasItems={filteredTags.length > 0}
                />
                <TableHead className="w-48">{t('tags.table.name', { ns: 'pages' })}</TableHead>
                <TableHead className="w-28">{t('common.table.columns.category')}</TableHead>
                {showColorColumn && (
                  <TableHead className="w-28">{t('common.table.columns.color')}</TableHead>
                )}
                <TableHead numeric className="w-28">{t('common.table.columns.usage')}</TableHead>
                {showCreatedAtColumn && (
                  <TableHead numeric className="w-32">{t('common.table.columns.createdAt')}</TableHead>
                )}
                <TableHead className="w-36 pl-10">{t('common.table.columns.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTags.length === 0 ? (
                <ListEmptyRow
                  colSpan={5 + (showColorColumn ? 1 : 0) + (showCreatedAtColumn ? 1 : 0)}
                  filtered={hasFilters}
                  title={t('tags.list.noTags', { ns: 'pages' })}
                  hint={t('tags.list.emptyHint', { ns: 'pages' })}
                  action={isAdmin(userRole) && (
                    <button type="button" onClick={handleAddTag} className={btnPrimary}>{t('tags.list.addButton', { ns: 'pages' })}</button>
                  )}
                />
              ) : (
                filteredTags.map((tag) => (
                  <TableRow key={tag.id}>
                    <BulkSelectCell
                      userRole={userRole}
                      isSelected={isItemSelected(tag.id)}
                      onToggle={() => toggleItemSelection(tag.id)}
                      canDelete={!(tag._count && tag._count.assignments > 0)}
                      disabledReason={tag._count && tag._count.assignments > 0 ? t('tags.list.cannotDeleteInUse', { ns: 'pages' }) : undefined}
                    />
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: tag.color }}
                        />
                        <span className="font-medium truncate">
                          {tag.name}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-fg-secondary">{getCategoryText(tag.category)}</span>
                    </TableCell>
                    {showColorColumn && (
                      <TableCell>
                        <div className="flex items-center gap-2.5">
                          <span
                            className="w-4 h-4 rounded border border-line flex-shrink-0"
                            style={{ backgroundColor: tag.color }}
                          />
                          <span className="text-sm font-mono text-fg-secondary select-all">
                            {tag.color}
                          </span>
                        </div>
                      </TableCell>
                    )}
                    <TableCell numeric>
                      {tag._count?.assignments || 0}
                      <span className="ml-1 text-[13px] text-fg-secondary">{t('common.forms.customers')}</span>
                    </TableCell>
                    {showCreatedAtColumn && (
                      <TableCell numeric className="!text-fg-secondary">
                        {formatTableDate(tag.createdAt, i18n.language)}
                      </TableCell>
                    )}
                    <TableCell className="pl-10">
                      {isAdmin(userRole) && (
                        <RowActions>
                          <RowAction onClick={() => handleEditTag(tag)}>{t('common.actions.edit')}</RowAction>
                          {/* 使用中的標籤不能刪：按鈕停用並以 title 說明原因 */}
                          <RowAction
                            tone="danger"
                            onClick={() => handleDelete(tag.id)}
                            disabled={Boolean(tag._count && tag._count.assignments > 0)}
                            title={tag._count && tag._count.assignments > 0 ? t('tags.list.cannotDeleteInUse', { ns: 'pages' }) : t('tags.list.deleteTag', { ns: 'pages' })}
                          >
                            {t('common.actions.delete')}
                          </RowAction>
                        </RowActions>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            </Table>
          </div>
        </div>
      )}

      <ListPagination pagination={displayPagination} onPageChange={changePage} isLoading={listLoading} className="mt-6" />

      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title={t('tags.list.confirmDeleteTitle', { ns: 'pages' })}
        message={t('tags.list.confirmDeleteMessage', { ns: 'pages' })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />

      <ConfirmModal
        isOpen={showBulkDeleteModal}
        onClose={closeBulkDeleteModal}
        onConfirm={confirmBulkDelete}
        title={t('common.bulk.confirmDeleteTitle', { count: selectedTags.length, entity: t('tags.item', { ns: 'pages' }) })}
        message={t('common.bulk.confirmDeleteMessage', { count: selectedTags.length, entity: t('tags.item', { ns: 'pages' }) })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isBulkDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />

      <TagDialog
        isOpen={showTagDialog}
        onClose={handleCloseTagDialog}
        tag={editingTag}
      />
    </div>
  );
};

export default TagListPage;